import React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import useLoyaltyCampaignOffers from 'loyalty/hooks/useLoyaltyCampaignOffers'

const Section = styled(Pane)`
  padding: ${rem(24)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(48)} 0;
  }
`

const OfferCard = styled(Pane)`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  height: 100%;
  border: none;
  min-width: ${rem(270)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(360)};
  }
`

const OfferImageContainer = styled.div`
  width: 100%;
  height: ${rem(180)};
  overflow: hidden;
`

function LoyaltyAccountCurrentOffersCarousel() {
  const intl = useIntl()
  const offers = useLoyaltyCampaignOffers()

  // Hide carousel if less than 3 offers
  if (offers.length < 3) return null

  // Show "View all" button only if more than 3 offers
  const showViewAllButton = offers.length > 3

  const sectionHeading = intl.formatMessage({
    id: 'ewJQ1786m9',
    defaultMessage: 'Current offers to earn & save more',
    description: 'Section heading for loyalty campaign offers carousel',
  })

  const viewAllLabel = intl.formatMessage({
    id: 'NzvsnZD09+',
    defaultMessage: 'View all',
    description: 'Button text to view all loyalty campaign offers',
  })

  const browseOffersLabel = intl.formatMessage({
    id: 'g3I24iyRvN',
    defaultMessage: 'Browse offers',
    description: 'Link text to browse loyalty campaign offers',
  })

  return (
    <Section background="neutral-eight">
      <LayoutContainer>
        <Group direction="vertical" gap={24}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
            <Heading variant="heading3">{sectionHeading}</Heading>
            {showViewAllButton && (
              <TextButton kind="tertiary" size="medium" to="/societe#offers">
                {viewAllLabel}
              </TextButton>
            )}
          </Group>
          <CardCarousel pageSize={3}>
            {offers.map(offer => {
              const title = intl.formatMessage(offer.title)
              return <OfferCard
                key={title}
                borderRadius="L"
              >
                <OfferImageContainer>
                  <Image id={offer.imageId} height={180} fit="center" />
                </OfferImageContainer>
                <Group direction="vertical" gap={12}>
                  <Heading variant="heading4" lineClamp={2}>{title}</Heading>
                  <BodyText variant="small" colour="neutral-two">{intl.formatMessage(offer.description)}</BodyText>
                  <TextLink size="medium" weight="regular" to={offer.link}>
                    {browseOffersLabel}
                  </TextLink>
                </Group>
              </OfferCard>
            })}
          </CardCarousel>
        </Group>
      </LayoutContainer>
    </Section>
  )
}

export default LoyaltyAccountCurrentOffersCarousel
